
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, AlertTriangle, RefreshCw, ExternalLink, Wallet, Info, AlertCircle } from 'lucide-react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useInterval } from '@/hooks/use-interval';
import { useAuthRefresh } from '@/hooks/useAuthRefresh';
import FinancialOverview from './finances/FinancialOverview';
import { ErrorRecovery } from './finances/ErrorRecovery';

interface FinancesManagerProps {
  userId: string;
}

type PayoutStatus = 'pending' | 'in_progress' | 'active' | 'restricted' | 'rejected';

interface PractitionerFinanceDetails {
  payout_details_status: PayoutStatus;
  stripe_account_id: string | null;
}

// Utility function to detect mobile devices
const isMobileDevice = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
         window.innerWidth <= 768;
};

const FinancesManager: React.FC<FinancesManagerProps> = ({ userId }) => {
  const { t } = useTranslation('finances');
  const [isLoading, setIsLoading] = useState(false);
  const [financeDetails, setFinanceDetails] = useState<PractitionerFinanceDetails | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [forceRefresh, setForceRefresh] = useState(false);
  const { refreshSession } = useAuthRefresh();
  
  const onboardingParam = searchParams.get('onboarding');
  const hasOnboardingParam = onboardingParam === 'refresh' || onboardingParam === 'success';

  const getStatusMessages = () => ({
    pending: {
      title: t('status.notConnected.title'),
      description: t('status.notConnected.description'),
      icon: <Info className="h-6 w-6 text-blue-500" />,
      color: 'border-blue-200 bg-blue-50'
    },
    in_progress: {
      title: t('status.inProgress.title'),
      description: t('status.inProgress.description'),
      icon: <RefreshCw className="h-6 w-6 text-amber-500" />,
      color: 'border-amber-200 bg-amber-50'
    },
    active: {
      title: t('status.active.title'),
      description: t('status.active.description'),
      icon: <CheckCircle className="h-6 w-6 text-green-500" />,
      color: 'border-green-200 bg-green-50'
    },
    restricted: {
      title: t('status.restricted.title'),
      description: t('status.restricted.description'),
      icon: <AlertTriangle className="h-6 w-6 text-red-500" />,
      color: 'border-red-200 bg-red-50'
    },
    rejected: {
      title: t('status.rejected.title'),
      description: t('status.rejected.description'),
      icon: <AlertTriangle className="h-6 w-6 text-red-500" />,
      color: 'border-red-200 bg-red-50'
    }
  });
  
  useEffect(() => {
    if (hasOnboardingParam) {
      setForceRefresh(true);
      fetchFinanceDetails(true);
      
      const newUrl = window.location.pathname + window.location.search.replace(/[?&]onboarding=[^&]+/, '');
      window.history.replaceState({}, '', newUrl);
      
      if (onboardingParam === 'success') {
        toast({
          title: t('toast.stripeSetupInitiated'),
          description: t('toast.stripeSetupInitiatedDescription'),
          variant: "default",
        });
      }
    }
  }, [hasOnboardingParam, location, t]);
  
  useInterval(() => {
    if (forceRefresh) {
      fetchFinanceDetails(false);
    }
  }, forceRefresh ? 10000 : null);
  
  useEffect(() => {
    if (forceRefresh) {
      const timer = setTimeout(() => {
        setForceRefresh(false);
      }, 120000);
      
      // Auto-stop refresh when status becomes active or if we've been refreshing too long
      if (financeDetails?.payout_details_status === 'active') {
        console.log('Stripe account is now active, stopping refresh');
        setForceRefresh(false);
        toast({
          title: "Stripe Setup Complete",
          description: "Your Stripe account is now active and ready to receive payments!",
          variant: "default",
        });
      }
      
      return () => clearTimeout(timer);
    }
  }, [forceRefresh, financeDetails, toast]);

  const fetchFinanceDetails = async (showToast = false) => {
    try {
      setIsLoadingData(true);
      setLastError(null);
      
      // First try to get real-time status from Stripe
      try {
        const { data: stripeCheck, error: stripeError } = await supabase.functions.invoke('check-stripe-status');
        if (stripeCheck && !stripeError) {
          console.log('Real-time Stripe status check result:', stripeCheck);
          if (stripeCheck.updated && showToast) {
            toast({
              title: "Status Updated",
              description: `Your Stripe account status has been updated to: ${stripeCheck.status}`,
              variant: "default",
            });
          }
        }
      } catch (stripeCheckError) {
        console.log('Stripe status check failed, falling back to database:', stripeCheckError);
      }
      
      // Always get the latest from database (may have been updated by Stripe check)
      const { data, error } = await supabase
        .from('practitioners')
        .select('payout_details_status, stripe_account_id')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching finance details:', error);
        setLastError(t('errors.failedToLoad'));
        toast({
          variant: "destructive",
          title: t('errors.connectionError'),
          description: t('errors.failedToLoad')
        });
      } else {
        setFinanceDetails({
          payout_details_status: data.payout_details_status as PayoutStatus,
          stripe_account_id: data.stripe_account_id
        });
        
        if (showToast) {
          toast({
            variant: "default",
            title: t('toast.statusUpdated'),
            description: t('toast.statusUpdatedDescription')
          });
        }
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setLastError(t('errors.unexpectedError'));
    } finally {
      setIsLoadingData(false);
      setRefreshingStatus(false);
    }
  };

  useEffect(() => {
    fetchFinanceDetails();
  }, [userId]);

  const handleConnectOrContinueStripe = async () => {
    setIsLoading(true);
    setLastError(null);
    
    try {
      console.log('Attempting to connect/continue Stripe setup...');
      
      // Check current session first
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          variant: "destructive",
          title: "会话已过期",
          description: "请重新登录后再试",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('create-connect-account', {
        method: 'POST'
      });

      if (error) {
        console.error('Error with Stripe operation:', error);
        
        // Check if it's an authentication error
        if (error.message?.includes('Authentication') || error.message?.includes('session')) {
          console.log('Authentication error detected, trying to refresh session...');
          const refreshed = await refreshSession();
          
          if (refreshed) {
            // Retry the operation after refresh
            const { data: retryData, error: retryError } = await supabase.functions.invoke('create-connect-account', {
              method: 'POST'
            });
            
            if (retryError) {
              setLastError(retryError.message || t('toast.connectionFailedDescription'));
              toast({
                variant: "destructive",
                title: t('toast.connectionFailed'),
                description: retryError.message || t('toast.connectionFailedDescription')
              });
              return;
            }
            
            // Process successful retry
            if (retryData && retryData.onboardingUrl) {
              processStripeUrl(retryData.onboardingUrl);
              return;
            }
          } else {
            toast({
              variant: "destructive",
              title: "认证失败",
              description: "请重新登录后再试",
            });
            return;
          }
        }
        
        setLastError(error.message || t('toast.connectionFailedDescription'));
        toast({
          variant: "destructive",
          title: t('toast.connectionFailed'),
          description: error.message || t('toast.connectionFailedDescription')
        });
        return;
      }

      if (data && data.onboardingUrl) {
        processStripeUrl(data.onboardingUrl);
      } else {
        setLastError(t('toast.invalidResponse'));
        toast({
          variant: "destructive",
          title: t('toast.connectionFailed'),
          description: t('toast.invalidResponse')
        });
      }
    } catch (err: any) {
      console.error('Unexpected error:', err);
      const errorMessage = err.message || t('toast.unexpectedError');
      setLastError(errorMessage);
      toast({
        variant: "destructive",
        title: t('toast.connectionFailed'),
        description: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  const processStripeUrl = (url: string) => {
    console.log('Opening Stripe onboarding URL:', url);
    
    // Check if it's a mobile device
    if (isMobileDevice()) {
      // On mobile, show a toast about the process and redirect in the same tab
      toast({
        variant: "default",
        title: t('toast.stripeSetup'),
        description: "您将被重定向到 Stripe 完成设置。完成后，您将自动返回到此页面。",
        duration: 5000
      });
      // Redirect in the same tab on mobile
      window.location.href = url;
    } else {
      // On desktop, open in a new tab
      window.open(url, '_blank');
      toast({
        variant: "default",
        title: t('toast.stripeSetup'),
        description: t('toast.stripeSetupDescription', { 
          action: financeDetails?.stripe_account_id ? t('toast.continueYourSetup') : t('toast.completeSetup') 
        })
      });
    }
    
    setForceRefresh(true);
  };

  const handleRefreshStatus = () => {
    setRefreshingStatus(true);
    fetchFinanceDetails(true);
  };

  if (isLoadingData) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  const getDisplayStatus = () => {
    if (forceRefresh) {
      return {
        status: 'in_progress',
        title: t('status.processing.title'),
        description: t('status.processing.description'),
        icon: <RefreshCw className="h-6 w-6 animate-spin text-amber-500" />,
        color: 'border-amber-200 bg-amber-50'
      };
    }
    
    const status = financeDetails?.payout_details_status || 'pending';
    return getStatusMessages()[status];
  };

  const getButtonText = () => {
    if (financeDetails?.payout_details_status === 'active') {
      return t('buttons.manageStripe');
    }
    
    if (financeDetails?.stripe_account_id) {
      return t('buttons.continueSetup');
    }
    
    return t('buttons.connectStripe');
  };

  const displayStatus = getDisplayStatus();
  const buttonText = getButtonText();
  const isStripeActive = financeDetails?.payout_details_status === 'active';

  return (
    <div className="space-y-6 pb-8 md:pb-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
          <p className="text-gray-500">{t('subtitle')}</p>
        </div>
        <div className="flex space-x-2">
          <Button 
            variant="outline"
            size="sm"
            onClick={handleRefreshStatus}
            disabled={refreshingStatus}
          >
            {refreshingStatus ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('refreshing')}</>
            ) : (
              <><RefreshCw className="mr-2 h-4 w-4" /> {t('refreshStatus')}</>
            )}
          </Button>
        </div>
      </div>

      {financeDetails?.payout_details_status === 'pending' && (
        <Alert className="border-blue-500 bg-blue-50">
          <AlertCircle className="h-5 w-5 text-blue-600" />
          <AlertTitle className="text-blue-900 font-semibold">
            {t('platformMigration.title', 'Stripe Platform Migration Required')}
          </AlertTitle>
          <AlertDescription className="text-blue-800">
            {t('platformMigration.description', 'Our platform has upgraded to Stripe Connect for improved payment processing. Please reconnect your Stripe account below to continue receiving payments.')}
          </AlertDescription>
        </Alert>
      )}

      <Card className={`${displayStatus.color} border-2`}>
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            {displayStatus.icon}
            <div>
              <h3 className="text-lg font-semibold">{displayStatus.title}</h3>
              <p className="text-sm text-gray-600">{displayStatus.description}</p>
              {isMobileDevice() && financeDetails?.payout_details_status !== 'active' && (
                <p className="text-xs text-amber-600 mt-2">
                  📱 手机端提示：点击连接后将跳转到 Stripe，完成设置后会自动返回并更新状态
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {lastError && (
        <ErrorRecovery 
          error={lastError}
          onRetry={() => {
            setLastError(null);
            fetchFinanceDetails(true);
          }}
          onRefreshSession={refreshSession}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Wallet className="mr-2 h-5 w-5" />
            {t('connection.title')}
          </CardTitle>
          <CardDescription>
            {t('connection.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {financeDetails?.stripe_account_id ? (
            <p className="text-sm text-gray-500 mb-4">
              {financeDetails.payout_details_status === 'active' 
                ? t('connection.fullySetup')
                : t('connection.inProgressSetup')}
            </p>
          ) : (
            <p className="text-sm text-gray-500 mb-4">
              {t('connection.needsConnection')}
            </p>
          )}
          
          {financeDetails?.stripe_account_id && financeDetails.payout_details_status === 'in_progress' && (
            <Alert className="mb-4">
              <Info className="h-4 w-4" />
              <AlertTitle>{t('connection.setupRequired.title')}</AlertTitle>
              <AlertDescription>
                {t('connection.setupRequired.description')}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
        <CardFooter>
          <Button 
            onClick={handleConnectOrContinueStripe}
            disabled={isLoading || forceRefresh}
            className="w-full md:w-auto"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('buttons.processing')}
              </>
            ) : forceRefresh ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('buttons.updatingStatus')}
              </>
            ) : (
              <>
                <ExternalLink className="mr-2 h-4 w-4" />
                {buttonText}
              </>
            )}
          </Button>
        </CardFooter>
      </Card>

      {isStripeActive ? (
        <FinancialOverview stripeAccountId={financeDetails.stripe_account_id} />
      ) : null}
    </div>
  );
};

export default FinancesManager;
